import { Link } from 'react-router-dom';
import { MapPin, Mail, Phone } from 'lucide-react';
import { Separator } from '@/Components/ui/separator';
import { siteConfig } from '@/config/site';

const quickLinks = [
    { label: 'About Us', href: '/about' },
    { label: 'Trading Platform', href: '/platform/overview' },
    { label: 'Funding Programs', href: '/funding/overview' },
    { label: 'Trading Tools', href: '/tools/economic-calendar' },
    { label: 'Partner Program', href: '/partners' },
    { label: 'Contact Us', href: '/contact' },
];

const legalLinks = [
    { label: 'Terms & Conditions', href: '/terms-and-conditions' },
    { label: 'Privacy Policy', href: '/privacy-policy' },
    { label: 'Risk Disclosure', href: '/risk-disclosure' },
    { label: 'AML/KYC Policy', href: '/aml-kyc-policy' },
    { label: 'Client Agreement', href: '/client-agreement' },
    { label: 'Cookie Policy', href: '/cookie-policy' },
];

export function Footer() {
    const restricted = siteConfig.restrictedRegions.join(', ');

    return (
        <footer className="border-t border-border bg-background">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
                    {/* Brand */}
                    <div>
                        <img
                            src={siteConfig.brand.logo.wordmark}
                            alt={siteConfig.brand.name}
                            className="h-10 w-auto mb-4"
                            width={1000}
                            height={300}
                        />
                        <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                            {siteConfig.brand.description}
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {siteConfig.social.map((s) => (
                                <a
                                    key={s.label}
                                    href={s.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-xs font-medium text-muted-foreground hover:text-foreground hover:border-primary/30 hover:bg-accent transition-all duration-200"
                                    title={s.label}
                                >
                                    {s.icon}
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div>
                        <h4 className="text-sm font-medium text-foreground mb-4 tracking-wide uppercase">Quick Links</h4>
                        <ul className="space-y-2.5">
                            {quickLinks.map((link) => (
                                <li key={link.href}>
                                    <Link to={link.href} className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200">
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Legal */}
                    <div>
                        <h4 className="text-sm font-medium text-foreground mb-4 tracking-wide uppercase">Legal</h4>
                        <ul className="space-y-2.5">
                            {legalLinks.map((link) => (
                                <li key={link.href}>
                                    <Link to={link.href} className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200">
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Contact */}
                    <div>
                        <h4 className="text-sm font-medium text-foreground mb-4 tracking-wide uppercase">Contact</h4>
                        <div className="space-y-4">
                            <div className="flex items-start gap-3">
                                <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                                <div className="text-sm text-muted-foreground">
                                    <p className="text-foreground/80">{siteConfig.contact.address.line1}</p>
                                    <p>{siteConfig.contact.address.line2}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                                <a href={`mailto:${siteConfig.contact.email.info}`} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                                    {siteConfig.contact.email.info}
                                </a>
                            </div>
                            <div className="flex items-center gap-3">
                                <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
                                <a href={`tel:${siteConfig.contact.phone}`} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                                    {siteConfig.contact.phone}
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Disclaimer */}
            <div className="border-t border-border">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
                    <div className="space-y-3 text-xs text-muted-foreground/70 leading-relaxed">
                        <p>
                            <span className="text-muted-foreground">Prop Trading Disclaimer:</span>{' '}
                            {siteConfig.brand.name} operates as a proprietary trading firm providing simulated trading
                            accounts for evaluation and funding purposes. All trading activities are conducted on
                            demo/simulated accounts using virtual funds, not real market investments.
                        </p>
                        <p>
                            <span className="text-muted-foreground">Restricted Regions:</span>{' '}
                            {siteConfig.brand.name} does not provide services for citizens/residents of {restricted}.
                            The services of {siteConfig.brand.name} are not intended for distribution to, or use by,
                            any person in any country or jurisdiction where such distribution or use would be contrary
                            to local law or regulation.
                        </p>
                    </div>

                    <Separator className="my-6" />

                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        <p className="text-xs text-muted-foreground/60">
                            &copy; {new Date().getFullYear()} {siteConfig.brand.legalName}. All rights reserved.
                        </p>
                        <p className="text-xs text-muted-foreground/60">
                            Developed in {siteConfig.developer.location} by{' '}
                            <a href={siteConfig.developer.url} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors">
                                {siteConfig.developer.name}
                            </a>
                        </p>
                    </div>
                </div>
            </div>
        </footer>
    );
}
